import { Fee } from "../../types/fee-types";

export function isAdditionalPetFee(fee: Fee) {
  return fee.label.toLowerCase().includes("additional");
}

// Basically if there is more than 1 pet fee it finds the one that makes the most sense to have the
// +- buttons by it and marks the rest of them as "isAdditionalPetFees == true;"
// so when ever a pet fee is added it should also add all fees with isAdditionalPetFees == true
/*--------------------------------------------------------------------------------------------------*/
function populatePetFeeAttributes(fees: Fee[]) {
  // Get all personal addon Pet Fees - all pet fees are optional
  const pets = fees.filter(
    (fee) =>
      fee.category === "pets" && fee.providerGroup === "Personalized Add-Ons",
  );
  let mainPetFee = null;
  if (pets.length > 1) {
    // first look for pet rent/monthly
    mainPetFee = pets.find(
      (pet) =>
        pet.type === "pet_rent_other" &&
        pet.frequency === "monthly" &&
        !isAdditionalPetFee(pet),
    );
    // then look for pet deposit
    mainPetFee ??= pets.find(
      (pet) => pet.type === "pet_rent_other" && !isAdditionalPetFee(pet),
    );
    // then look for pet deposit
    mainPetFee ??= pets.find(
      (pet) => pet.type === "pet_deposit_other" && !isAdditionalPetFee(pet),
    );
    // then look for pet other
    mainPetFee ??= pets.find(
      (pet) => pet.type === "pet_other" && !isAdditionalPetFee(pet),
    );
    // if one is found set all other pet fees in the fees array to isAdditional
    if (mainPetFee) {
      const mainpet: Fee = mainPetFee;
      return fees.map((fee) => {
        if (
          fee.category === "pets" &&
          fee.providerGroup === "Personalized Add-Ons" &&
          fee.id !== mainpet.id
        ) {
          fee.isAdditionalPetFee = true;
        }
        return fee;
      });
    }
  }

  return fees;
}

// Format raw Engrain fees as Fee[]
/*--------------------------------------------------------------------------------------------------*/
export function getAllFees(feesList: Fee[]) {
  const allFees: Fee[] = feesList.map((fee) => {
    const newFee: Fee = {
      ...fee,
      activeCount: fee.isRequired ? 1 : 0,
      isAdditionalPetFee: fee.isAdditionalPetFee ?? false,
    };

    return newFee;
  });

  return populatePetFeeAttributes(allFees);
}

// Logic if a fee is enabled
/*--------------------------------------------------------------------------------------------------*/
function isFeeEnabled(fee: Fee) {
  return fee.isEnabled;
}

// Business logic to include a fee in the Move In Basics Category
/*--------------------------------------------------------------------------------------------------*/
export function isFeeEssential(fee: Fee) {
  // Essential fee must be enabled
  if (!isFeeEnabled(fee)) return false;
  // Provider group of a Essential fee must equal Essentials
  const AllowedProviderGroups = ["Essentials"];

  return AllowedProviderGroups.includes(fee.providerGroup);
}

// Business logic to include a fee in the Personal Add On Category
/*--------------------------------------------------------------------------------------------------*/
export function isFeePersonalizedAddOn(fee: Fee) {
  // Add on fee must be enabled
  if (!isFeeEnabled(fee)) return false;
  // Provider group of a Personal add on fee Must be Personal Add-On
  const AllowedProviderGroups = ["Personalized Add-Ons"];

  return AllowedProviderGroups.includes(fee.providerGroup);
}

// Returns Fee Group Maps
/*--------------------------------------------------------------------------------------------------*/
export function getFeeGroupMap(fees: Fee[]) {
  const feeMap = new Map<string, Fee[]>();
  fees?.forEach((fee) => {
    let key = "";
    if (fee.category === "rentable_items" && !fee.isRequired) {
      key = fee.group;
    } else if (fee.providerGroup === "Essentials") {
      key = "Other essentials";
    } else {
      key = fee.category;
    }

    const collection = feeMap.get(key);
    if (!collection) {
      feeMap.set(key, [fee]);
    } else {
      collection.push(fee);
    }
  });
  return feeMap;
}

// Business logic to include a fee in the total monthly leasing cost
/*--------------------------------------------------------------------------------------------------*/
export function isFeeMonthly(fee: Fee) {
  // Fee must be enabled
  if (!isFeeEnabled(fee)) return false;
  // Frequency of a TMLP fee must be monthly
  const AllowedFrequency = ["monthly"];
  // Provider group of a TMLP fee must equal Essentials, Personal Add-Ons OR MoveIn Basics
  const AllowedProviderGroups = [
    "Essentials",
    "Personalized Add-Ons",
    "Move-in Basics",
  ];
  // Amount must be numeric to be calculated in TMLP
  const AllowedValueTypes = ["amount", "range", "percentage", "text"];

  return (
    AllowedFrequency.includes(fee.frequency) &&
    AllowedProviderGroups.includes(fee.providerGroup) &&
    AllowedValueTypes.includes(fee.valueType)
  );
}

// Business logic to include a fee in the one time fees
/*--------------------------------------------------------------------------------------------------*/
export function isFeeOneTime(fee: Fee) {
  // one time fee must be enabled
  if (!isFeeEnabled(fee)) return false;
  // Frequency of a one time fee fee must be one_time, annual, or per lease
  const AllowedFrequency = ["one_time", "annually", "per_lease"];
  // Provider group of a One Time Fee must equal Essentials, Personal Add-Ons OR MoveIn Basics
  const AllowedProviderGroups = [
    "Essentials",
    "Personalized Add-Ons",
    "Move-in Basics",
  ];
  // Amount must be numeric to be calculated in One Time Fees
  const AllowedValueTypes = ["amount", "range", "percentage", "text"];

  return (
    AllowedFrequency.includes(fee.frequency) &&
    AllowedProviderGroups.includes(fee.providerGroup) &&
    AllowedValueTypes.includes(fee.valueType)
  );
}

// Business logic for per applicant fees
// The assumption is a lease holder and applicant imply the same business logic related to pricing
/*--------------------------------------------------------------------------------------------------*/
export function isFeePerApplicant(fee: Fee) {
  return (
    fee.providerMethod === "Per Leaseholder" ||
    fee.providerMethod === "Per Applicant" ||
    fee.providerMethod === "Per Resident"
  );
}

// Business logic to show the counter on the calculator for a specific fee
/*--------------------------------------------------------------------------------------------------*/
export function isFeeCountModifiable(fee: Fee) {
  if (fee.isRequired) return false;
  return (
    (isFeePersonalizedAddOn(fee) && !fee.isAdditionalPetFee) ||
    isFeePerApplicant(fee)
  );
}

// Calculate max limit based on value cap
/*--------------------------------------------------------------------------------------------------*/
export function getValueCapLimit(fee: Fee) {
  const valCap = parseFloat(fee?.valueCap ?? "0");
  const amount = fee.amount ?? 0;
  if (valCap > 0) {
    return Math.floor(amount / valCap);
  }
  return 0;
}

// Get Max Fee Limit
/*--------------------------------------------------------------------------------------------------*/
export function getFeeLimitMax(fee: Fee) {
  // limit based on data field in Engrain
  const valCapLimit = getValueCapLimit(fee);
  return valCapLimit > 0 ? valCapLimit : parseInt(fee.limit);
}

// Get Min Fee Limit
/*--------------------------------------------------------------------------------------------------*/
export function getFeeLimitMin(fee: Fee) {
  // currently we are only imposing min fee for application fees
  return isFeePerApplicant(fee) && fee.isRequired ? 1 : 0;
}

// Returns all Monthly Essential Fees
/*--------------------------------------------------------------------------------------------------*/
export function getAllMonthlyRequiredEssentialFees(allFees: Fee[]) {
  return allFees.filter(
    (fee) =>
      isFeeEssential(fee) && fee.frequency === "monthly" && fee.isRequired,
  );
}

// Returns all Monthly Fees except for montly fees for additional pets
/*--------------------------------------------------------------------------------------------------*/
export function getMonthlyFees(allFees: Fee[]) {
  return allFees?.filter((fee) => isFeeMonthly(fee) && !fee.isAdditionalPetFee);
}

// Returns Monthly Fee Groups and puts "Other Essentials" at the end"
/*--------------------------------------------------------------------------------------------------*/
export function getMonthlyFeeGroups(allFees: Fee[]) {
  const fees = getMonthlyFees(allFees);
  return getFeeGroupMap(fees);
}

// Returns all fees that required or have been selected
/*--------------------------------------------------------------------------------------------------*/
export function isFeeRequiredOrSelected(fee: Fee) {
  return fee.isRequired || fee.activeCount > 0;
}

// Returns all One Time Fees
/*--------------------------------------------------------------------------------------------------*/
export function getOneTimeFees(allFees: Fee[]) {
  return allFees?.filter((fee) => isFeeOneTime(fee));
}

// Returns One Time Fee Groups
/*--------------------------------------------------------------------------------------------------*/
export function getOneTimeFeeGroupMap(allFees: Fee[]) {
  const fees = getOneTimeFees(allFees);
  return getFeeGroupMap(fees);
}

// Returns all One Time Application Fees  (In step 3 it filters out optional fees that haven't been chosen)
/*--------------------------------------------------------------------------------------------------*/
/*--------------------------------------------------------------------------------------------------*/
export function getOneTimeApplicationFees(allFees: Fee[], step?: number) {
  return allFees.filter(
    (fee) =>
      isFeeOneTime(fee) &&
      fee.dueAtTiming === "application" &&
      (step === 3 ? isFeeRequiredOrSelected(fee) : fee.isRequired),
  );
}

// Returns all One Time MoveIn Fees (In step 3 it filters out optional fees that haven't been chosen)
/*--------------------------------------------------------------------------------------------------*/
export function getOneTimeMoveInFees(allFees: Fee[], step?: number) {
  return allFees.filter(
    (fee) =>
      isFeeOneTime(fee) &&
      fee.dueAtTiming === "move_in" &&
      (step === 3 ? isFeeRequiredOrSelected(fee) : fee.isRequired),
  );
}

// Returns all One Time Move_out Fees (In step 3 it filters out optional fees that haven't been chosen)
/*--------------------------------------------------------------------------------------------------*/
export function getOneTimeMoveOutFees(allFees: Fee[]) {
  return allFees.filter(
    (fee) =>
      isFeeOneTime(fee) &&
      fee.dueAtTiming === "move_out" &&
      isFeeRequiredOrSelected(fee),
  );
}

// Returns all Personal Add On Fees
/*--------------------------------------------------------------------------------------------------*/
export function getAllPersonalizedAddonFees(allFees: Fee[]) {
  return allFees.filter((fee) => isFeePersonalizedAddOn(fee));
}

// Returns Personal Add On Fee Groups
/*--------------------------------------------------------------------------------------------------*/
export function getPersonalizedAddonGroupMap(allFees: Fee[]) {
  const fees = getAllPersonalizedAddonFees(allFees);
  return getFeeGroupMap(fees);
}

// Returns selected personalized add ons for step 3 and all for other steps. Places "Other Essentials" at the end for step 1
/*--------------------------------------------------------------------------------------------------*/
export const getSelectedSelectedPersonalizedItems = (
  entries: [string, Fee[]][],
  step: number,
) => {
  if (step !== 3) {
    const nonOtherEssentials = entries.filter(
      ([groupName]) => groupName !== "Other essentials",
    );
    const otherEssentials = entries.find(
      ([groupName]) => groupName === "Other essentials",
    );
    if (otherEssentials) {
      nonOtherEssentials.push(otherEssentials);
    }
    return nonOtherEssentials;
  }

  return entries
    .map(([groupName, fees]) => {
      const filteredFees = fees.filter(
        (fee) => fee.activeCount !== 0 && fee.frequency === "monthly",
      );
      return [groupName, filteredFees] as [string, Fee[]];
    })
    .filter(([, fees]) => fees.length > 0);
};

// * Checks if there is an optional fee with a 'monthly' frequency for the same category.
export function optionalFeeHasMonthly(allFees: Fee[], fee: Fee) {
  return allFees.some(
    (feeItem) =>
      feeItem.category === fee.category && feeItem.frequency === "monthly",
  );
}
/**
 * Filters one-time fees based on specific criteria.
 *
 * Steps:
 * 1. Retrieve one-time fees from the full list using getOneTimeFees(allFees).
 * 2. For each fee:
 *    - If the fee's providerGroup is not "Personalized Add-Ons", include it.
 *    - If the fee is an additional pet fee and its activeCount is 0, exclude it.
 *    - If the fee is an additional pet fee and there is no corresponding monthly fee (determined by optionalFeeHasMonthly), exclude it.
 *    - Otherwise, include the fee.
 *
 */
export function getOneTimeStepOneFees(allFees: Fee[]) {
  const oneTimeFees = getOneTimeFees(allFees);

  const oneTimeStepFees = oneTimeFees?.filter((fee) => {
    const hasMonthlyFee = optionalFeeHasMonthly(allFees, fee);
    if (fee.providerGroup !== "Personalized Add-Ons" && !fee.isRequired) {
      return false;
    }
    if (fee.providerGroup !== "Personalized Add-Ons") {
      return true;
    }
    if (fee.isAdditionalPetFee && fee.activeCount === 0) {
      return false;
    }
    if (!hasMonthlyFee && fee.isAdditionalPetFee) {
      return false;
    }
    return true;
  });
  return oneTimeStepFees;
}

// Returns pet fees that are one time or an addiitonal pet fee .
/*--------------------------------------------------------------------------------------------------*/
export const getOneTimePetFees = (
  allFees: Fee[],
  fee?: Fee,
  count: number = 0,
) => {
  if (!fee) return [];
  if (count > 0) {
    const matchingAdditionalPetFee = allFees.filter(
      (feeItem) =>
        feeItem.isAdditionalPetFee &&
        feeItem.providerGroup === "Personalized Add-Ons" &&
        feeItem.group === fee.group &&
        feeItem.group === "pets_other" &&
        feeItem.id !== fee.id &&
        (isFeeMonthly(feeItem) || fee.frequency === "one_time")
    );
    return matchingAdditionalPetFee;
  } else {
    return allFees.filter(
      (feeItem) =>
        (isFeeOneTime(feeItem) || feeItem.isAdditionalPetFee) &&
        feeItem.providerGroup === "Personalized Add-Ons" &&
        feeItem.group === fee.group &&
        feeItem.group === "pets_other" &&
        feeItem.id !== fee.id,
    );
  }
};
